import axios from "axios";
import { notification, Modal } from "antd";
import Logger from "../utils/logger";
import { getApiConfig } from "../utils/organizationConfig";
import tenantResolver from "./tenantResolver";

// Get API configuration from environment variable first, fallback to production defaults
const getServerURL = () => {
  // Use environment variable if available (for development/production)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback to production defaults based on environment
  // DO NOT use getApiConfig().baseUrl as it's for tenant-specific endpoints, not main API
  if (process.env.NODE_ENV === 'production') {
    return 'https://inferno-api.bitsmith.in';
  }
  
  // Development default
  return 'https://ffss-dev-api.bitsmith.in';
};

export const serverURL = getServerURL();

// HTTP Methods enum
export const HTTP_METHODS = {
  GET: 1,
  POST: 2,
  PATCH: 3,
  DELETE: 4,
  PUT: 5,
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: serverURL,
  timeout: 30000, // 30 seconds
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant header for multi-tenant support
    let tenantId = null;
    
    // Priority 1: Get from userData (set after login)
    try {
      const userDataStr = localStorage.getItem("userData");
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        if (userData?.tenantId) {
          tenantId = userData.tenantId;
        }
      }
    } catch (error) {
      Logger.warn('Failed to get tenantId from userData', error);
    }
    
    // Priority 2: Get from separate tenant storage (fallback)
    if (!tenantId) {
      try {
        const tenantStr = localStorage.getItem("tenant");
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr);
          tenantId = tenant?.tenantId || tenant?.slug;
        }
      } catch (error) {
        Logger.warn('Failed to get tenant from storage', error);
      }
    }
    
    // Priority 3: Use tenant resolver if available
    if (!tenantId && tenantResolver.isInitialized()) {
      tenantId = tenantResolver.getCurrentTenantId();
    }
    
    // REMOVE or make conditional the final fallback to avoid wrong tenant usage
    if (!tenantId) {
      // Only use default in development, otherwise warn
      if (process.env.NODE_ENV === 'development') {
        tenantId = process.env.REACT_APP_DEFAULT_TENANT || 'ffss-development';
        Logger.warn('Using default tenant in development:', tenantId);
      } else {
        Logger.error('No tenant ID found - request may fail');
        // Don't set a default in production to catch configuration issues
      }
    }
    
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    Logger.debug('API Request:', {
      url: config.url,
      method: config.method,
      data: config.data,
      tenantId: tenantId,
    });
    return config;
  },
  (error) => {
    Logger.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    Logger.debug('API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  async (error) => {
    const { response } = error;
    
    // Handle 401 Unauthorized
    if (response?.status === 401) {
      const hasAccessToken = localStorage.getItem("accessToken");
      
      if (hasAccessToken) {
        // Only show session expired modal if user was previously logged in
        Logger.warn('Session expired, logging out user');
        await handleUnauthorized();
      } else {
        // For login attempts or when not logged in, just show error notification
        // Logger.warn('Authentication failed');
        // notification.error({
        //   message: 'Authentication Failed',
        //   description: response?.data?.message || 'Invalid credentials',
        //   placement: 'bottom',
        //   duration: 2,
        // });
      }
      return Promise.reject(error);
    }

    // Handle other errors
    Logger.error('API Error:', {
      url: error.config?.url,
      status: response?.status,
      message: error.message,
      data: response?.data,
    });

    return Promise.reject(error);
  }
);

const handleUnauthorized = () => {
  return new Promise((resolve) => {
    Modal.warning({
      title: 'Session Expired',
      content: 'Your session has expired. Please login again.',
      okText: 'OK',
      closable: false,
      maskClosable: false,
      keyboard: false,
      onOk: () => {
        // Clear all auth data
        localStorage.clear();
        sessionStorage.clear();
        
        // Redirect to login
        window.location.href = '/';
        resolve();
      },
    });
  });
};

export const getAuthHeaders = () => {
  const accessToken = localStorage.getItem("accessToken");
  return {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
};

const APICall = async (url, data, method, authorization = true) => {
  try {
    const headers = authorization ? getAuthHeaders() : {};
    let response;

    switch (method) {
      case HTTP_METHODS.GET:
        response = await axiosInstance.get(url, headers);
        break;
      case HTTP_METHODS.POST:
        response = await axiosInstance.post(url, data, headers);
        break;
      case HTTP_METHODS.PATCH:
        response = await axiosInstance.patch(url, data, headers);
        break;
      case HTTP_METHODS.DELETE:
        response = await axiosInstance.delete(url, headers);
        break;
      case HTTP_METHODS.PUT:
        response = await axiosInstance.put(url, data, headers);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return response;
  } catch (error) {
    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;
      
      // Show user-friendly error message only for 5xx errors
      if (status >= 500 || status === 401) {
        notification.error({
          message: 'Server Error',
          description: data?.message || 'Something went wrong. Please try again.',
          placement: 'topRight',
          duration: 4.5,
        });
      }
      
      // Log all errors to console
      Logger.error('API Error:', {
        status,
        message: data?.message || error.message,
        url: error.config?.url,
      });
    }
    
    throw error;
  }
};

// Export the APICall function
export default APICall;

/**
 * Fetch list of active tenants for login selection
 * @returns {Promise<Array>} Array of tenant objects with tenantId, slug, and name
 */
export const fetchTenants = async () => {
  try {
    const response = await axiosInstance.get('/auth/tenants', {
      headers: {
        // Don't include Authorization header for this public endpoint
      }
    });
    return response.data.tenants || [];
  } catch (error) {
    Logger.error('Error fetching tenants:', error);
    throw error;
  }
};
